import { BaseDragger } from "./BaseDragger";
import { DraggerType, type Point2D, type Point3D } from "./types";
import { PREVIEW_LINE_STYLE } from "./previewStyles";
import { insertTextEntity } from "./textInsertion";

/** Radius of the marker showing where the text will land, in px. */
const MARKER_RADIUS = 6;

/**
 * Dragger for text entry through the device handwriting keyboard.
 *
 * Recognition is delegated to the tablet itself: iPad (Scribble / Apple
 * Pencil) and Samsung (S Pen handwriting) convert stylus writing inside any
 * focused text field, offline and in the user's language. This dragger only
 * handles the drawing side of it:
 *
 * 1. the user taps the drawing -> the position is stored and listeners are
 *    notified, so the UI can open the text modal;
 * 2. the user writes with the stylus in that field and validates;
 * 3. insertText() places the MText at the tapped position, sized by the
 *    active scale config (same path as every other text tool).
 *
 * A marker stays on the drawing while the modal is open so the user can see
 * where the text will land.
 */
export class StylusKeyboardDragger extends BaseDragger {
  readonly type = DraggerType.StylusKeyboard;

  /** Position awaiting text, in world coordinates. */
  private pendingWorld: Point3D | null = null;
  private markerId: string | null = null;
  private listeners = new Set<() => void>();

  // ============== Public API (via StylusKeyboardManager) ==============

  /**
   * Subscribe to placement requests: fired when the user has picked a
   * position and the text input should open. Returns an unsubscribe function.
   */
  onPlacementRequest(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Whether a position is waiting for its text. */
  hasPendingPlacement(): boolean {
    return this.pendingWorld !== null;
  }

  /** Insert the text at the pending position (modal validated). */
  insertText(text: string): void {
    const world = this.pendingWorld;
    this.cancelPlacement();
    const trimmed = text.trim();
    if (!world || !trimmed) return;

    insertTextEntity(this.viewer, this.module, {
      via: "keyboard",
      worldX: world.x,
      worldY: world.y,
      text: trimmed,
      config: this.viewer.activeNoteConfig,
      source: "StylusKeyboardDragger.insertText",
    });
  }

  /** Forget the pending position (modal cancelled). */
  cancelPlacement(): void {
    this.pendingWorld = null;
    this.removeMarker();
  }

  // ============== Lifecycle ==============

  protected onDeactivate(): void {
    this.cancelPlacement();
  }

  // ============== Dragger Callbacks ==============

  protected onStart(): void {}

  protected onDrag(): void {}

  protected onEnd(screen: Point2D, world: Point3D): void {
    this.pendingWorld = world;
    this.showMarker(screen);
    for (const listener of this.listeners) listener();
  }

  protected onCancel(): void {
    this.cancelPlacement();
    super.onCancel();
  }

  // ============== Marker ==============

  private showMarker(screen: Point2D): void {
    this.removeMarker();
    this.markerId =
      this.viewer.overlayManager?.createPoint(
        screen.x,
        screen.y,
        MARKER_RADIUS,
        {
          ...PREVIEW_LINE_STYLE,
          fillColor: PREVIEW_LINE_STYLE.strokeColor,
        },
      ) ?? null;
  }

  private removeMarker(): void {
    if (this.markerId) {
      this.viewer.overlayManager?.remove(this.markerId);
      this.markerId = null;
    }
  }
}
